import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { RiMapPinAddLine, RiUploadCloud2Line } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BinBblEntryForm } from '../../components/onboarding/BinBblEntryForm';
import { PortfolioSummary } from '../../components/NextGenDashboard/PortfolioSummary';
import type { ICompliancePropertyStatus } from '../../services/zeos-api/contracts/Compliance';
import type { IIncentivePropertySignals } from '../../services/zeos-api/contracts/Incentive';
import { PortfolioTile } from '../../components/Portfolio/PortfolioTile';
import { PropertyImageUpload } from '../../components/Portfolio/PropertyImageUpload';
import { MeteringAttestationPrompt } from '../../components/property/MeteringAttestationPrompt';
import { PropertyMetersPanel } from '../../components/property/PropertyMetersPanel';
import { BulkPropertyUpload } from '../../components/Shared/BulkPropertyUpload';
import { zeosDialog, zeosSuccess } from '../../components/Shared/confirm-dialog/ConfirmDialog';
import { ZeosButton } from '../../components/Shared/ZeosButton';
import { ZeosModal } from '../../components/Shared/ZeosModal';
import { ZeosSpinner } from '../../components/Shared/ZeosSpinner';
import { CarbonClearEmbed } from '../../components/utility-link/CarbonClearEmbed';
import { DerapiLinkSelector } from '../../components/utility-link/DerapiLinkSelector';
import { UtilityLinkSelector } from '../../components/utility-link/UtilityLinkSelector';
import { AuthContext } from '../../context/AuthContext';
import { ZeosApiContext } from '../../context/ZeosApiContext';
import { PropertyForm } from '../../forms/PropertyForm';
import { useFormValidation } from '../../hooks/useFormValidation';
import { IPortfolioItem } from '../../mock-data/portfolioData';
import { setSelectedProperty } from '../../redux-store/PortfolioSlice';
import { IZeosUser } from '../../services/auth/IZeosUser';
import { defaultPropertyForm } from '../../services/forms/DefaultFormData';
import { IPropertyFormDataType } from '../../services/forms/FormDataTypes';
import {
    PropertyValidationSchema
} from '../../services/forms/validationSchemas/PropertyValidationSchema';
import { FeatureFlags } from '../../configs/FeatureFlags';
import {
    buildComplianceStatusMap, PortfolioModalType, projReducer
} from '../../services/helpers/PortfolioHelpers';
import {
    IComplianceStatusResult, mapPropertyContractToFormData, mapPropertyFormToContract,
    uploadPropertyImages
} from '../../services/helpers/PropertyHelpers';
import { contentWrapperClass } from '../../services/helpers/ThemeHelpers';
import { handleOpenLinkUtilityAsync } from '../../services/helpers/UtilityLinkHelpers';
import { createLogger } from '../../services/Logger';
import { pageConfigBySlug } from '../../services/nav/NavigationService';
import { ICompanyClaims } from '../../services/zeos-api/contracts/Account';
import { IListItemSimple } from '../../services/zeos-api/contracts/ListUtility';
import {
    CarbonClearGovEnum, CarbonClearTestEnum
} from '../../services/zeos-api/enums/AutoCompliance';
import {
    ZeosListEnum, ZeosListOptionEnum
} from '../../services/zeos-api/enums/ListUtility';
import { DashboardFooter } from './layout/DashboardFooter';
import { DashboardHeader } from './layout/DashboardHeader';

export const PortfolioScreen = () => {
  const auth = useContext(AuthContext);
  const api = useContext(ZeosApiContext);
  const log = createLogger("Portfolio");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCompany, setActiveCompany] = useState<ICompanyClaims>();
  const [activeModalType, setActiveModalType] = useState<PortfolioModalType>(
    PortfolioModalType.None
  );
  const [activeProperty, setActiveProperty] = useState<IPortfolioItem | null>(
    null
  );
  const [activePropertyForModal, setActivePropertyForModal] =
    useState<IPortfolioItem | null>(null);
  const [carbonClearBin, setCarbonClearBin] = useState<string | undefined>(
    undefined
  );
  const [companyList, setCompanyList] = useState<IListItemSimple[]>([]);
  const [complianceStatusMap, setComplianceStatusMap] = useState<
    Map<number, IComplianceStatusResult>
  >(new Map());
  // Per-property status for the card's signal chips (task-4035).
  const [signalComplianceMap, setSignalComplianceMap] = useState<
    Map<number, ICompliancePropertyStatus>
  >(new Map());
  const [incentiveSignalMap, setIncentiveSignalMap] = useState<
    Map<number, IIncentivePropertySignals>
  >(new Map());
  // True from the start of a property load until the incentive signals call settles (task-4110).
  const [incentiveSignalsLoading, setIncentiveSignalsLoading] = useState(true);
  const [hvacOptions, setHvacOptions] = useState<IListItemSimple[]>([]);
  const [priorWorkTypeOptions, setPriorWorkTypeOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [isUpdatingBinBbl, setIsUpdatingBinBbl] = useState<boolean>(false);
  const [loadingProperty, setLoadingProperty] = useState<string | null>(null);
  const [originalFormData, setOriginalFormData] =
    useState<IPropertyFormDataType | null>(null);
  const [portfolioItems, setPortfolioItems] = useState<IPortfolioItem[]>([]);
  // The company portfolioItems were loaded for, so the edit deep link never searches a stale list.
  const [portfolioCompanyId, setPortfolioCompanyId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const [showBinBblModal, setShowBinBblModal] = useState<boolean>(false);
  const [showBulkAddModal, setShowBulkAddModal] = useState<boolean>(false);
  const [showCarbonClearModal, setShowCarbonClearModal] =
    useState<boolean>(false);
  const [zeosUser, setZeosUser] = useState<IZeosUser>();
  const [manageableMeterPropertyIds, setManageableMeterPropertyIds] = useState<Set<number>>(new Set());
  const propertyLoadIdRef = useRef(0);

  const { formData, setFormData, errors, formValidate, formChange } =
    useFormValidation<IPropertyFormDataType>(
      PropertyValidationSchema,
      defaultPropertyForm
    );

  const searchedItems = portfolioItems.filter((x) =>
    x.name.toLowerCase().includes(searchText.toLowerCase())
  );

  // Check if form data has been modified by user
  const checkFormDirty = useCallback(() => {
    if (!originalFormData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  // Update form dirty state when form data changes
  useEffect(() => {
    if (originalFormData) {
      const dirty = checkFormDirty();
      setIsFormDirty(dirty);
    }
  }, [formData, checkFormDirty, originalFormData]);

  useEffect(() => {
    const initAsync = async () => {
      const user = await auth.zeosUserAsync();
      setZeosUser(user);
    };
    initAsync();
  }, [auth]);

  // Landing spot for the Derapi join-callback redirect (see DerapiController.JoinCallback).
  useEffect(() => {
    const connected = searchParams.get("connected");
    if (connected === null) {
      return;
    }
    if (connected === "false") {
      const message = searchParams.get("error");
      zeosDialog(
        message ? `Connection failed: ${message}` : "Connection failed."
      );
    } else {
      zeosDialog("Power generation provider connected.");
    }
    searchParams.delete("connected");
    searchParams.delete("error");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadHvacOptions = async () => {
      try {
        const response = await api.ListUtility.getListAsync(
          ZeosListEnum.HvacPrimaryType,
          ZeosListOptionEnum.excludeUtility
        );
        setHvacOptions(response.data ?? []);
      } catch (error) {
        console.error("Failed to load HVAC system type options", error);
      }
    };
    loadHvacOptions();
  }, []);

  useEffect(() => {
    const loadPriorWorkTypeOptions = async () => {
      try {
        const response = await api.ListUtility.getListAsync(
          ZeosListEnum.BuildingPriorWorkType
        );
        setPriorWorkTypeOptions(
          response.data?.map((item) => ({
            id: item.dataKey,
            name: item.display,
          })) ?? []
        );
      } catch (error) {
        log.error("Failed to load prior work type options", { error });
      }
    };

    loadPriorWorkTypeOptions();
  }, []);

  useEffect(() => {
    const initAsync = async () => {
      activeCompany && (await fetchProperties(activeCompany.companyId));
    };
    initAsync();
  }, [activeCompany]);

  // Which tiles get a "Meters" button (task-4062): company Admins and super admins, minus single-meter
  // properties for company Admins (R1). The API decides; nothing shows if the call fails.
  useEffect(() => {
    let cancelled = false;
    const loadManageableAsync = async () => {
      setManageableMeterPropertyIds(new Set());
      if (!activeCompany?.companyId) {
        return;
      }
      try {
        const response = await api.Meter.getManageablePropertyIdsAsync(activeCompany.companyId);
        if (!cancelled && response.zeosStatus?.toLowerCase() === "success") {
          setManageableMeterPropertyIds(new Set(response.data ?? []));
        }
      } catch (error) {
        log.warn("Could not load meter access", error);
      }
    };
    loadManageableAsync();
    return () => {
      cancelled = true;
    };
  }, [activeCompany?.companyId, api]);

  const handleSelectedPortfolio = (property: any) =>
    dispatch(setSelectedProperty(property));

  const fetchProperties = async (companyId: number) => {
    const loadId = ++propertyLoadIdRef.current;
    try {
      setIsLoadingProperties(true);
      setComplianceStatusMap(new Map());
      setSignalComplianceMap(new Map());
      setIncentiveSignalMap(new Map());
      setIncentiveSignalsLoading(true);
      const response = await api.Property.propertiesByCompanyId(companyId);
      // Sandbox clones are withheld by the API for non-super-admins, so no filtering here.
      const props = response.data ?? [];
      const mapped: IPortfolioItem[] = props.map((p) => ({
        companyId: p.companyId ?? 0,
        companyName: activeCompany?.companyName ?? "",
        image: p.blobImageUrl,
        investment: projReducer(p.projects, "investment"),
        name: p.propertyName,
        paidBack: projReducer(p.projects, "paidBack"),
        profitability: projReducer(p.projects, "profitability"),
        propertyId: p.propertyId ?? 0,
        revenue: projReducer(p.projects, "revenue"),
      }));
      if (loadId !== propertyLoadIdRef.current) {
        return;
      }
      setPortfolioItems(mapped);
      setPortfolioCompanyId(companyId);
      setIsLoadingProperties(false);

      void (async () => {
        try {
          const summary = await api.Compliance.getPortfolioSummary(companyId);
          if (loadId === propertyLoadIdRef.current) {
            setSignalComplianceMap(
              new Map(
                (summary.data?.properties ?? []).map((x) => [x.propertyId, x])
              )
            );
          }
        } catch (error) {
          // Chips for this card are simply omitted when the summary can't load.
          log.error("Failed to load compliance summary", error);
        }
      })();

      void (async () => {
        try {
          const signals = await api.Incentive.portfolioSignals(companyId);
          if (loadId === propertyLoadIdRef.current) {
            setIncentiveSignalMap(
              new Map(
                (signals.data?.properties ?? []).map((x) => [x.propertyId, x])
              )
            );
          }
        } catch (error) {
          // Cards fall back to "No data" when the signals can't load.
          log.error("Failed to load incentive signals", error);
        } finally {
          if (loadId === propertyLoadIdRef.current) {
            setIncentiveSignalsLoading(false);
          }
        }
      })();

      void (async () => {
        try {
          const complianceMap = await buildComplianceStatusMap({
            api,
            log,
            properties: mapped,
          });
          if (loadId === propertyLoadIdRef.current) {
            setComplianceStatusMap(complianceMap);
          }
        } catch (error) {
          log.error("Failed to load compliance statuses", error);
        }
      })();
    } catch (error) {
      log.error("Failed to load properties", error);
      setPortfolioItems([]);
      if (loadId === propertyLoadIdRef.current) {
        setPortfolioCompanyId(companyId);
      }
      setComplianceStatusMap(new Map());
      setSignalComplianceMap(new Map());
      setIncentiveSignalMap(new Map());
      setIncentiveSignalsLoading(false);
    } finally {
      if (loadId === propertyLoadIdRef.current) {
        setIsLoadingProperties(false);
      }
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleOpenUploadImage = (property: IPortfolioItem) => {
    setActiveProperty(property);
    setActiveModalType(PortfolioModalType.UploadImage);
  };

  const handleOpenAddProperty = async () => {
    if (!zeosUser) {
      return;
    }
    const customerId = zeosUser.customerClaims[0]?.customerId;
    if (!customerId) {
      return;
    }
    const result = await api.Company.companiesByCustIdAsync(customerId);
    if (result?.data?.length) {
      const mappedCompanies: IListItemSimple[] = result.data.map((c) => ({
        dataKey: c.companyId!,
        display: c.companyName,
        value: String(c.companyId),
      }));
      setCompanyList(mappedCompanies);
    }
    setFormData(defaultPropertyForm);
    setOriginalFormData(null);
    setIsFormDirty(false);
    setActiveModalType(PortfolioModalType.AddProperty);
  };

  // Deep link from a screening run held on utilities (UTL P07): opens that property's editor,
  // where the utility selectors and panel live. `company` names the property's company; the header
  // already switches to it from the URL, so wait until that company's properties are loaded.
  useEffect(() => {
    const editPropertyId = Number(searchParams.get("editProperty") ?? 0);
    if (!editPropertyId || !activeCompany?.companyId || !zeosUser) {
      return;
    }
    const wantedCompanyId = Number(searchParams.get("company") ?? 0) || null;
    const userHasWantedCompany =
      !!wantedCompanyId &&
      (zeosUser.customerClaims ?? []).some((customer) =>
        (customer.companies ?? []).some((company) => company.companyId === wantedCompanyId)
      );
    if (userHasWantedCompany && activeCompany.companyId !== wantedCompanyId) {
      return;
    }
    if (portfolioCompanyId !== activeCompany.companyId) {
      return;
    }
    const target = portfolioItems.find((item) => item.propertyId === editPropertyId);
    searchParams.delete("editProperty");
    setSearchParams(searchParams, { replace: true });
    if (target) {
      void handleOpenEditProperty(target);
    } else {
      zeosDialog(
        "We couldn't open that property. It isn't in this portfolio, or you don't have access to it."
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioItems, portfolioCompanyId, activeCompany?.companyId, zeosUser]);

  const handleOpenEditProperty = async (property: IPortfolioItem) => {
    if (!property.propertyId) {
      zeosDialog("No property ID available");
      return;
    }
    try {
      const response = await api.Property.getProperty(property.propertyId);
      if (response.message === "error") {
        zeosDialog("Failed to load property data");
        return;
      }
      const propertyData = response.data;
      if (propertyData) {
        const companyId = activeCompany?.companyId ?? property.companyId ?? 0;
        const mappedFormData = mapPropertyContractToFormData(propertyData, companyId);
        setFormData(mappedFormData);
        setOriginalFormData(mappedFormData);
        setIsFormDirty(false);
        setActiveProperty(property);
        setActiveModalType(PortfolioModalType.EditProperty);
      }
    } catch (error) {
      console.error("Failed to load property data", error);
      zeosDialog("Failed to load property data");
    }
  };

  const handleSaveClick = () => {
    formValidate(handleSaveProperty);
  };

  const handleSaveProperty = async () => {
    // Check if form is dirty when editing
    // An open AMBIGUOUS utility whose shown value is a candidate is confirmed by saving as-is.
    if (
      activeModalType === PortfolioModalType.EditProperty &&
      !isFormDirty &&
      !formData.servingUtilityConfirmationPending
    ) {
      zeosDialog("No changes to save");
      return;
    }

    const companyId = activeCompany?.companyId ?? 0;
    const isEditMode =
      formData.propertyId !== undefined && formData.propertyId > 0;
    const payload = mapPropertyFormToContract(
      formData,
      companyId,
      isEditMode ? formData.propertyId : undefined
    );

    try {
      const result = isEditMode
        ? await api.Property.putProperty(payload)
        : await api.Property.postProperty(payload);

      const propertyId = result.data?.propertyId as number;
      if (propertyId && formData.file && formData.file.length > 0) {
        await uploadPropertyImages(api, propertyId, formData.file);
      }
      await zeosSuccess(
        isEditMode ? "Property updated." : "Property created.",
        async () => {
          closeModal();
          setFormData(defaultPropertyForm);
          setOriginalFormData(null);
          setIsFormDirty(false);
          await fetchProperties(payload.companyId);
        }
      );
    } catch (error) {
      console.error("Property save failed", error);
      zeosDialog("Failed to save property");
    }
  };

  const handleOpenLinkUtility = async (property: IPortfolioItem) => {
    setActiveProperty(property);
    const linkType = await handleOpenLinkUtilityAsync(property, api);
    setActiveModalType(linkType);
  };

  const handleOpenConnectPowerGeneration = (property: IPortfolioItem) => {
    setActiveProperty(property);
    setActiveModalType(PortfolioModalType.DerapiLink);
  };

  const handleOpenMeters = (property: IPortfolioItem) => {
    setActiveProperty(property);
    setActiveModalType(PortfolioModalType.Meters);
  };

  const handleOpenBinBblModal = (property: IPortfolioItem) => {
    setActivePropertyForModal(property);
    setShowBinBblModal(true);
  };

  const handleOpenCarbonClearModal = async (property: IPortfolioItem) => {
    //HACK: BIN/BBL SHOULD BE ADDED IPORTFOLIOITEM
    if (!property.propertyId) {
      zeosDialog("No property ID available");
      return;
    }
    try {
      setActivePropertyForModal(property);
      const response = await api.Property.getProperty(property.propertyId);
      if (response.message === "error") {
        zeosDialog("Failed to load property data");
        return;
      }
      const propertyData = response.data;
      if (propertyData) {
        setCarbonClearBin(propertyData.bin || undefined);
        setShowCarbonClearModal(true);
      }
    } catch (error) {
      console.error("Failed to load property data", error);
      zeosDialog("Failed to load property data");
    }
  };

  const handleCarbonClearClosed = async () => {
    const propertyId = activePropertyForModal?.propertyId;
    const companyId =
      activeCompany?.companyId ?? activePropertyForModal?.companyId;

    try {
      if (propertyId) {
        await api.AutoCompliance.carbonClearGovernance({
          propertyId,
          type: CarbonClearTestEnum.Create,
        });

        if (companyId) {
          await fetchProperties(companyId);
        }
      } else {
        log.warn("CarbonClear close: missing propertyId");
      }
    } catch (error) {
      log.error("Failed to update Carbon Clear governance", error);
      zeosDialog("Failed to refresh Carbon Clear status.");
    } finally {
      setShowCarbonClearModal(false);
      setActivePropertyForModal(null);
      setCarbonClearBin(undefined);
    }
  };

  const handleBinBblUpdate = async (bin?: string, bbl?: string) => {
    if (!activePropertyForModal || !activePropertyForModal.propertyId) {
      return;
    }
    setIsUpdatingBinBbl(true);
    try {
      if (bin || bbl) {
        const binBblUpdate = await api.Property.updateBblBin(
          activePropertyForModal.propertyId,
          bbl,
          bin
        );
        // Re-fetch properties to refresh the list
        const companyId =
          activeCompany?.companyId ?? activePropertyForModal.companyId;
        if (companyId) {
          await fetchProperties(companyId);
        }
      }
    } catch (error) {
      log.error("Error updating BIN/BBL:", error);
      zeosDialog("Failed to update BIN/BBL");
    } finally {
      setIsUpdatingBinBbl(false);
      setShowBinBblModal(false);
      setActivePropertyForModal(null);
    }
  };

  const handleClick = async (property: IPortfolioItem) => {
    setLoadingProperty(property.name);
    handleSelectedPortfolio(property);
    setLoadingProperty(null);
    navigate(
      `/${pageConfigBySlug("dashboard")?.slug || "dashboard"}?${"company"}=${
        property.companyId
      }&${"property"}=${property.propertyId}`
    );
  };

  const handleOpenIncentives = (property: IPortfolioItem) => {
    handleSelectedPortfolio(property);
    navigate(
      `/${pageConfigBySlug("incentives")?.slug || "incentives"}?company=${
        property.companyId
      }&property=${property.propertyId}`
    );
  };

  // Shared modal close handler
  const closeModal = () => {
    // Store the current modal type before closing
    const currentModal = activeModalType;

    // Close the modal first
    setActiveModalType(PortfolioModalType.None);

    // Reset form data based on modal type that was open
    if (currentModal === PortfolioModalType.AddProperty) {
      setFormData(defaultPropertyForm);
      setOriginalFormData(null);
      setIsFormDirty(false);
    } else if (currentModal === PortfolioModalType.EditProperty) {
      setFormData(defaultPropertyForm);
      setOriginalFormData(null);
      setIsFormDirty(false);
      setActiveProperty(null);
    } else if (
      currentModal === PortfolioModalType.UploadImage ||
      currentModal === PortfolioModalType.Meters
    ) {
      setActiveProperty(null);
    }
  };

  // Render modal content based on type
  const renderModalContent = () => {
    switch (activeModalType) {
      case PortfolioModalType.AddProperty:
      case PortfolioModalType.EditProperty:
        return (
          <>
            <div
              className={`${contentWrapperClass(
                "form"
              )} max-h-[80vh] overflow-y-auto`}
            >
              <PropertyForm
                formData={formData}
                errors={errors}
                handleChange={formChange}
                setFormData={setFormData}
                showCompanyDropdown={false}
                companyList={companyList}
                hvacOptions={hvacOptions}
                priorWorkTypeOptions={priorWorkTypeOptions}
                showServingUtilitySelectors
                showMatchingFacts
                showAddressLookup={
                  activeModalType !== PortfolioModalType.EditProperty
                }
              />
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <ZeosButton
                label={
                  activeModalType === PortfolioModalType.AddProperty
                    ? "Save Property"
                    : "Save Changes"
                }
                type="button"
                mode="action"
                onPress={handleSaveClick}
              />
              <ZeosButton
                label="Cancel"
                type="button"
                mode="prev"
                onPress={closeModal}
              />
            </div>
          </>
        );
      case PortfolioModalType.UploadImage:
        if (!activeProperty?.propertyId) {
          return null;
        }
        return (
          <div
            className={`${contentWrapperClass(
              "form"
            )} max-h-[80vh] overflow-y-auto`}
          >
            <PropertyImageUpload
              propertyId={activeProperty.propertyId}
              propertyName={activeProperty.name}
              companyId={
                activeCompany?.companyId ?? activeProperty.companyId ?? 0
              }
              onSuccess={async () => {
                const companyId =
                  activeCompany?.companyId ?? activeProperty.companyId;
                if (companyId) {
                  await fetchProperties(companyId);
                }
                closeModal();
              }}
              onCancel={closeModal}
            />
          </div>
        );
      case PortfolioModalType.UapiLink:
      case PortfolioModalType.BayouLink:
        return (
          <UtilityLinkSelector
            property={activeProperty}
            zeosUser={zeosUser}
            onClose={closeModal}
          />
        );
      case PortfolioModalType.DerapiLink:
        return (
          <DerapiLinkSelector
            property={activeProperty}
            zeosUser={zeosUser}
            onClose={closeModal}
          />
        );
      case PortfolioModalType.Meters:
        if (!activeProperty?.propertyId) {
          return null;
        }
        return <PropertyMetersPanel propertyId={activeProperty.propertyId} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <DashboardHeader companyCallback={setActiveCompany} />
      <main className="flex-grow font-sans">
        <section className="main py-6 px-8">
          {FeatureFlags.enablePortfolioSummary ? (
            <PortfolioSummary companyName={activeCompany?.companyName} />
          ) : null}
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-2xl font-sans inline-flex items-center text-zeos-black">
                Properties
              </span>
              <div className="h-[2px] w-6 bg-zeos-orange-dk mt-1"></div>
            </div>
            <div className="relative w-[250px]">
              <input
                type="text"
                placeholder="Search"
                value={searchText}
                onChange={handleSearch}
                className="border border-gray-300 rounded-md px-3 py-2 w-full"
              />
            </div>
          </div>
          {isLoadingProperties ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <ZeosSpinner message="Loading properties..." />
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-[90%]"
              style={{ paddingLeft: "0", paddingRight: "0" }}
            >
              {searchedItems.map((property) => {
                const complianceStatus = complianceStatusMap.get(
                  property.propertyId
                );
                log.info("complianceStatus", complianceStatus);
                return (
                  <PortfolioTile
                    key={property.propertyId?.toString()}
                    property={property}
                    complianceStatus={signalComplianceMap.get(
                      property.propertyId
                    )}
                    incentiveSignalsLoading={incentiveSignalsLoading}
                    incentiveSignals={incentiveSignalMap.get(
                      property.propertyId
                    )}
                    loadingProperty={loadingProperty}
                    handleClick={() => handleClick(property)}
                    onEditProperty={handleOpenEditProperty}
                    onUploadImage={handleOpenUploadImage}
                    onLinkUtility={handleOpenLinkUtility}
                    onOpenIncentives={handleOpenIncentives}
                    offerCarbonClear={
                      complianceStatus?.offerCarbonClear &&
                      complianceStatus.carbonClearStatus ===
                        CarbonClearGovEnum.None
                    }
                    carbonClearStatus={complianceStatus?.carbonClearStatus}
                    onOpenCarbonClearModal={handleOpenCarbonClearModal}
                    onOpenMeters={
                      manageableMeterPropertyIds.has(property.propertyId)
                        ? handleOpenMeters
                        : undefined
                    }
                  />
                );
              })}
              {/* Add Property Card */}
              <div
                className="flex items-center justify-center border-2 border-dashed border-gray-400 rounded-md h-[150px] mt-9 cursor-pointer"
                onClick={handleOpenAddProperty}
              >
                <div className="flex">
                  <RiMapPinAddLine size={32} className="text-gray-500" />
                  <p className="text-lg text-gray-500 mt-[5px]">Add Property</p>
                </div>
              </div>
              {/* Add Property in Bulk Card */}
              {FeatureFlags.enableBulkPropertyAdd && (
                <div
                  className="flex items-center justify-center border-2 border-dashed border-gray-400 rounded-md h-[150px] mt-9 cursor-pointer"
                  onClick={() => {
                    if (!activeCompany?.companyId) {
                      return;
                    }
                    setShowBulkAddModal(true);
                  }}
                >
                  <div className="flex">
                    <RiUploadCloud2Line size={32} className="text-gray-500" />
                    <p className="text-lg text-gray-500 mt-[5px]">
                      Add Property in Bulk
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </main>
      <DashboardFooter />

      {/* Consolidated Modal */}
      <ZeosModal
        isOpen={activeModalType !== PortfolioModalType.None}
        onClose={closeModal}
      >
        {renderModalContent()}
      </ZeosModal>

      {/* Bulk Add Property Modal */}
      <BulkPropertyUpload
        isOpen={showBulkAddModal}
        onClose={() => setShowBulkAddModal(false)}
        companyId={activeCompany?.companyId ?? 0}
        companyName={activeCompany?.companyName}
        onCreated={async () => {
          if (activeCompany?.companyId) {
            await fetchProperties(activeCompany.companyId);
          }
        }}
      />

      {/* BinBbl Entry Modal */}
      <BinBblEntryForm
        isOpen={showBinBblModal}
        onClose={() => {
          setShowBinBblModal(false);
          setActivePropertyForModal(null);
        }}
        onSubmit={handleBinBblUpdate}
        isLoading={isUpdatingBinBbl}
        propertyName={activePropertyForModal?.name || "this property"}
      />

      {/* Carbon Clear Modal */}
      <ZeosModal
        isOpen={showCarbonClearModal}
        onClose={handleCarbonClearClosed}
      >
        <CarbonClearEmbed
          bin={carbonClearBin}
          onCloseSelector={handleCarbonClearClosed}
          propertyId={activePropertyForModal?.propertyId}
          zeosUserId={zeosUser?.zeosUserId}
        />
      </ZeosModal>

      {/* One-time metering attestation prompt (task-4048; Portfolio for now, OQ-7) */}
      <MeteringAttestationPrompt />
    </div>
  );
};
